import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, Spin, notification } from "antd";
import { useNavigate } from "react-router-dom";
import AuthService from "../../base/services/authentication.service";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { getCurrentOrganizationLevel } from "../../base/services/accountLevel.service";
import { toAbsoluteUrl } from "../../../_metronic/_helpers";
import { billingApi, isFlowSwitchedOff } from "./billingApi";
import {
   BillingCode,
   BillingReason,
   SUPPORT_EMAIL,
   SubscriptionStatus,
   describeBillingError,
   describeBillingReason
} from "./billingCodes";
import {
   BillingAction,
   FixOutcome,
   cancelGoesThroughSupport,
   describeSavedCard,
   isHeldForPlan,
   offersCancelOnBillingPage,
   offersTrial,
   resolveBillingAction,
   resolveFixOutcome
} from "./billingActions";
import { formatBillingDate, formatBillingDateTime, isBillingDateAhead } from "./billingDates";
import { isBillingOnly, isHeldByGate, useBillingStore } from "./billingStore";
import { describeCard } from "./cards";
import CardSetup from "./CardSetup";
import { isEnterprisePacket, isEnterpriseRequestOpen } from "./enterprise";
import CancelSubscription from "../Payments/CancelSubscription";
import PaymentActionPanel from "./PaymentActionPanel";
import PlanPicker from "./PlanPicker";
import RetrySavedCard from "./RetrySavedCard";
import SettleDebtPanel from "./SettleDebtPanel";
import { describeUnpaidPlanEnd } from "./amountDue";
import "./index.scss";

// The billing page: where a signed-in organization held at billing_only lands,
// whichever way it got there — a sign in that reported it, a 402 in the middle
// of a session, an organization picked from the list. The routes show it in
// place of everything else until the organization's status says "full" again,
// and then take it straight back into the app (Routes.js).
//
// It has a layout of its own, the logo and a way out and nothing else, because
// the app's header and menus call endpoints the 402 gate refuses — they would
// only raise the same notice again underneath it.
//
// What it offers depends on why the organization is held (the status's
// `reason`), and it re-reads that status from Stripe as it opens: the one it was
// sent here with may already be out of date — a webhook can save a card that a
// browser closed on.
//
// When a 402 sent it here and the status says nothing is owed, the gate and the
// status disagree, and the page says so and waits to be asked to try again,
// rather than hand the organization straight back for the gate to refuse
// (billingStore.js).

// After a fix, the status can take a while to say so — Stripe settles invoices
// on its own schedule — so it is read again a few times before the page admits
// it is still waiting.
const POLL_INTERVAL_MS = 3000;
const POLL_ATTEMPTS = 10;

function ReasonBanner({ status }) {
   const copy = describeBillingReason(status?.reason, status);
   const card = describeCard(status?.paymentMethod);
   const graceEnds = formatBillingDate(status?.graceEndsAt);

   // Access is already held here, so what the grace period still protects is
   // the subscription itself.
   let grace = null;
   if (status?.reason === BillingReason.PAYMENT_FAILED && graceEnds) {
      grace = isBillingDateAhead(status.graceEndsAt)
         ? `Settle it before ${graceEnds} to keep your subscription.`
         : `The grace period for this payment ended on ${graceEnds}.`;
   }

   // The card on file is named where it answers a question: which card a
   // payment failed on, and, over the plans, that a card kept from before (or
   // just saved) is still there. It is not asked for again before a plan is
   // picked.
   // A card the status knows is there but can't name yet (an old customer's,
   // until the backend has synced it) is still said to be on file.
   const namesCard =
      status?.reason === BillingReason.PAYMENT_FAILED ||
      status?.reason === BillingReason.OUTSTANDING_DEBT ||
      isHeldForPlan(status);
   const hasCard = Boolean(card) || Boolean(status?.hasPaymentMethod);
   // An unpaid annual plan is set to end at its contract end unless paid.
   const planEnd = describeUnpaidPlanEnd(status);

   return (
      <div className="billing-banner" role="status">
         <h1 className="billing-banner__title">{copy.title}</h1>
         <p className="billing-banner__text">{copy.description}</p>
         {grace && <p className="billing-banner__text billing-banner__text--strong">{grace}</p>}
         {planEnd && <p className="billing-banner__text billing-banner__text--strong">{planEnd}</p>}
         {hasCard && namesCard && (
            <p className="billing-banner__text">
               {card ? `Card on file: ${card}.` : "You have a card on file."}
            </p>
         )}
      </div>
   );
}

// A 402 is holding the organization, and its billing status says it should be
// let in: after a payment, the gate can take a while to hear of it.
function AccessHeldPanel({ organizationName, onTryAgain }) {
   return (
      <>
         <div className="billing-banner" role="status">
            <h1 className="billing-banner__title">Your access hasn't caught up yet</h1>
            <p className="billing-banner__text">
               Your billing shows nothing left to settle, but {organizationName || "this organization"}{" "}
               is still on hold. If a payment has only just gone through, this can take a minute.
            </p>
         </div>
         <section className="billing-card">
            <div className="billing-actions billing-actions--center">
               <Button className="kapSurveyPrimaryButton" onClick={onTryAgain}>
                  Try again
               </Button>
            </div>
            <p className="billing-note billing-note--center">
               Still on hold after a few minutes? Contact us at{" "}
               <a href="mailto:info@kaptechgroup.com">info@kaptechgroup.com</a> and we'll help.
            </p>
         </section>
      </>
   );
}

function BillingShell({ organizationName, hasOtherOrganizations, isWide, children }) {
   const navigate = useNavigate();

   return (
      <div className="billing-shell">
         <header className="billing-shell__header">
            <img
               alt="KAPsurvey"
               className="billing-shell__logo"
               src={toAbsoluteUrl("/media/logos/kapSurvey-logo.png")}
            />
            <div className="billing-shell__side">
               {organizationName && (
                  <span className="billing-shell__organization">{organizationName}</span>
               )}
               {hasOtherOrganizations && (
                  <Button type="link" onClick={() => navigate("/organizations")}>
                     Switch organization
                  </Button>
               )}
               <Button className="defaultButton" onClick={AuthService.logout}>
                  Sign out
               </Button>
            </div>
         </header>
         <main className={`billing-shell__main${isWide ? " billing-shell__main--wide" : ""}`}>
            {children}
         </main>
      </div>
   );
}

function BillingRequiredPage() {
   const organizationId = AuthService.getOrganizationId();
   const organizationName = LocalStorageService.getItem("organizationName");
   const hasOtherOrganizations = (AuthService.getUser()?.organizations?.length ?? 0) > 1;

   const status = useBillingStore((state) => state.statuses[organizationId]);
   const isGateHeld = useBillingStore((state) => Boolean(state.gateHolds[organizationId]));
   const [isLoading, setIsLoading] = useState(true);
   const [loadError, setLoadError] = useState(null);
   const [isWaiting, setIsWaiting] = useState(false);
   const [stillWaiting, setStillWaiting] = useState(false);
   const [useDifferentCard, setUseDifferentCard] = useState(false);
   // The bank's reason for turning the last payment down, until the next fix,
   // and a count of refusals that starts the fix on screen afresh under it.
   const [paymentRefusal, setPaymentRefusal] = useState(null);
   const [refusals, setRefusals] = useState(0);
   // Choosing a plan again over the one a card is asked for
   // (PAYMENT_METHOD_REQUIRED), and the promo code the last pick on this page
   // was made with: a pick made again replaces it, and has to send it again.
   const [isChoosingPlan, setIsChoosingPlan] = useState(false);
   // The cancel dialog, offered for a subscription left unpaid.
   const [isCancelling, setIsCancelling] = useState(false);
   const [pickedPromoCode, setPickedPromoCode] = useState("");
   const pollTimer = useRef(null);

   const readStatus = useCallback(async () => {
      try {
         const fresh = await billingApi.getBillingStatus(organizationId);
         setLoadError(null);
         return fresh;
      } catch (failure) {
         // The new flow was switched off again. The read has already let the
         // organization go (billingApi.js), and the routes return it to the
         // app, which is the old flow's to run.
         if (isFlowSwitchedOff(failure)) return null;

         // The page is only routed to whoever may manage this billing
         // (Routes.js). The server says otherwise — a role taken away since
         // the organization was selected — so the routes take them out of it.
         if (failure?.code === BillingCode.NOT_ORGANIZATION_ADMIN) {
            const store = useBillingStore.getState();
            store.rememberBillingRight(organizationId, false);
            store.setCanManageBilling(false);
            return null;
         }

         setLoadError(failure);
         return null;
      }
   }, [organizationId]);

   useEffect(() => {
      readStatus().finally(() => setIsLoading(false));
      return () => clearTimeout(pollTimer.current);
   }, [readStatus]);

   // Said once, as the organization is let back in. The routes take it from
   // here: the status in the store is "full", so this page is replaced.
   const announceAccess = useCallback((next) => {
      notification.success({
         message:
            next?.subscriptionStatus === SubscriptionStatus.TRIALING
               ? "Free trial started successfully"
               : isEnterprisePacket(next?.packetId)
               ? "Enterprise plan started successfully"
               : "Subscription restored successfully",
         description: "You're all set — welcome back to KAPsurvey."
      });
   }, []);

   // An ended annual plan's balance paid. Nothing starts with it: the plans
   // come next, which the page now shows.
   const announceDebtPaid = useCallback(() => {
      notification.success({
         message: "Balance paid successfully",
         description: "Your previous plan is settled. Pick a plan to start a new subscription."
      });
   }, []);

   const waitForAccess = useCallback(
      (action, attempt = 0) => {
         pollTimer.current = setTimeout(async () => {
            const fresh = await readStatus();

            if (fresh && !isBillingOnly(fresh)) {
               setIsWaiting(false);
               announceAccess(fresh);
               return;
            }

            // Still held, but for something else now: a balance paid leaves an
            // ended subscription, held for a plan. The page shows that fix.
            if (fresh && !fresh.requiresAction && resolveBillingAction(fresh) !== action) {
               setIsWaiting(false);
               if (action === BillingAction.SETTLE_DEBT) announceDebtPaid();
               return;
            }

            // Something new is asked of the cardholder — a bank's check — which
            // the page now shows instead of the wait.
            if (fresh?.requiresAction || attempt + 1 >= POLL_ATTEMPTS) {
               setIsWaiting(false);
               setStillWaiting(!fresh?.requiresAction);
               return;
            }

            waitForAccess(action, attempt + 1);
         }, POLL_INTERVAL_MS);
      },
      [readStatus, announceAccess, announceDebtPaid]
   );

   // Held by a 402 alone: the status, read fresh, says nothing is owed — or no
   // longer answers at all.
   const isAccessHeld = !isLoading && isGateHeld && !isBillingOnly(status);

   // The 402's notice named a reason the status no longer gives, and this page
   // says what is going on instead.
   useEffect(() => {
      if (isAccessHeld) notification.destroy("billing-required");
   }, [isAccessHeld]);

   const action = resolveBillingAction(status);
   const isRepickingPlan = isChoosingPlan && status?.reason === BillingReason.PAYMENT_METHOD_REQUIRED;
   // The plan picker lays out columns of its own, as wide as the package step's.
   const showsPlans =
      !isLoading &&
      !isWaiting &&
      (action === BillingAction.START_SUBSCRIPTION ||
         action === BillingAction.RESUBSCRIBE ||
         action === BillingAction.ENTERPRISE_OFFER ||
         isRepickingPlan);

   // What every fix reports back: the status it left the organization in, and
   // the bank's refusal when it turned the payment down. `action` is still the
   // fix that was on screen when it started.
   const handleResolved = (next, outcome) => {
      setUseDifferentCard(false);
      setPaymentRefusal(null);

      const outcomeKind = resolveFixOutcome(action, next, outcome);

      // The balance is paid when what it leaves is no longer a balance to settle.
      if (
         action === BillingAction.SETTLE_DEBT &&
         outcomeKind === FixOutcome.SHOW_NEXT &&
         !next?.requiresAction &&
         resolveBillingAction(next) !== BillingAction.SETTLE_DEBT
      ) {
         announceDebtPaid();
      }

      switch (outcomeKind) {
         case FixOutcome.LET_IN:
            announceAccess(next);
            break;
         case FixOutcome.WAIT:
            // The same fix is still asked for: it went through, and the status
            // has not caught up yet.
            setStillWaiting(false);
            setIsWaiting(true);
            waitForAccess(action);
            break;
         case FixOutcome.DECLINED:
            // Said here rather than by the form that asked: the status the bank
            // left may already have put a different fix in its place. Whatever
            // is on screen starts afresh, so the reason is said once.
            setPaymentRefusal(
               outcome.error?.message || describeBillingError({ code: BillingCode.PAYMENT_FAILED })
            );
            setRefusals((count) => count + 1);
            break;
         case FixOutcome.DECLINED_STILL_IN: {
            // The routes take the organization back into the app, away from
            // this page, so it is said where it will still be seen — with when
            // the grace period that lets it in runs out, when the status says.
            const accessEnds = isBillingDateAhead(next.graceEndsAt)
               ? formatBillingDateTime(next.graceEndsAt)
               : null;
            notification.warning({
               message: "Heads up!",
               description: `Your bank didn't approve the payment, so it's still due. You can settle it from Subscription.${
                  accessEnds ? ` Full access until ${accessEnds}, then only billing is available.` : ""
               }`
            });
            break;
         }
         default:
            // A status that asks for something else — a card after a plan, a
            // bank's check after a card, a plan after a card — is simply
            // shown, which the store already did.
            break;
      }
   };

   // The card form reports only once the card is saved. A card saved with no
   // subscription for it to pay (Stripe no longer has the one there was) is on
   // file all the same, and the plans take the form's place. Saying so keeps
   // the form from seeming to just vanish.
   const handleCardSaved = (next, outcome) => {
      // After a balance, the plans are announced as the balance paid.
      if (!outcome?.error && isHeldForPlan(next) && action !== BillingAction.SETTLE_DEBT) {
         notification.success({
            message: "Card saved successfully",
            description: describeSavedCard(next)
         });
      }

      handleResolved(next, outcome);
   };

   // A pick that left the organization waiting on Enterprise terms: Enterprise
   // asked for with none agreed yet. Nothing went through for Stripe to
   // confirm, so it is not waited on; the page shows the wait for our team.
   const isWaitingOnEnterprise = (next, outcome) =>
      !outcome?.error && next?.reason === BillingReason.ENTERPRISE_OFFER_PENDING;

   const announceEnterpriseRequest = (next) => {
      setPaymentRefusal(null);
      if (!isEnterpriseRequestOpen(next)) return;
      notification.success({
         message: "Enterprise requested successfully",
         description: "Our team will contact you to agree the terms of your Enterprise plan."
      });
   };

   // A plan picked from the plans. Its promo code is kept for a pick made
   // again, which replaces this one, code and all.
   const handlePlanPicked = (next, outcome) => {
      setPickedPromoCode(outcome?.promoCode ?? "");
      if (isWaitingOnEnterprise(next, outcome)) {
         announceEnterpriseRequest(next);
         return;
      }
      handleResolved(next, outcome);
   };

   // A plan picked again over the one a card is asked for. The waiting pick is
   // overwritten, and a status that still asks for a card now names the new
   // plan, so the card form starts afresh for it. Not a fix that went through
   // and is waited on: the same fix is asked for because nothing was paid yet.
   // Anything else — a card Stripe already held, charged at once, or the bank's
   // check on it — is taken as any fix's result.
   const handlePlanRepicked = (next, outcome) => {
      setIsChoosingPlan(false);
      setPickedPromoCode(outcome?.promoCode ?? "");
      if (!outcome?.error && next?.reason === BillingReason.PAYMENT_METHOD_REQUIRED) return;
      if (isWaitingOnEnterprise(next, outcome)) {
         announceEnterpriseRequest(next);
         return;
      }
      handleResolved(next, outcome);
   };

   const checkAgain = async () => {
      setPaymentRefusal(null);
      setStillWaiting(false);
      setIsLoading(true);
      const fresh = await readStatus();
      setIsLoading(false);
      // Still held by a 402, it is not back in yet, whatever the status says.
      if (fresh && !isBillingOnly(fresh) && !isHeldByGate(organizationId)) announceAccess(fresh);
   };

   const renderAction = () => {
      switch (action) {
         case BillingAction.CONFIRM_PAYMENT:
            return useDifferentCard ? (
               <CardSetup
                  organizationId={organizationId}
                  status={status}
                  onDone={handleCardSaved}
               />
            ) : (
               <PaymentActionPanel
                  organizationId={organizationId}
                  status={status}
                  onResolved={handleResolved}
                  onUseDifferentCard={() => setUseDifferentCard(true)}
               />
            );
         // A trial only while the organization has its one left: after it,
         // the same plans are paid from the start (offersTrial). With no trial
         // left and no card on file, picking a plan asks for a card next
         // (PAYMENT_METHOD_REQUIRED), which the page then shows.
         case BillingAction.START_SUBSCRIPTION:
            // No subscription, so no kind of account settled either: both are
            // offered.
            return (
               <PlanPicker
                  organizationId={organizationId}
                  organizationLevel={undefined}
                  offersTrial={offersTrial(status)}
                  billingStatus={status}
                  onSubscribed={handlePlanPicked}
               />
            );
         // An annual plan that ended unpaid: its balance first, the plans after.
         // The card on file pays it, or a new card (which pays it as it is
         // saved, the consent naming the amount). Never the plans here.
         case BillingAction.SETTLE_DEBT:
            return status?.hasPaymentMethod && !useDifferentCard ? (
               <SettleDebtPanel
                  organizationId={organizationId}
                  status={status}
                  onResolved={handleResolved}
                  onUseDifferentCard={() => setUseDifferentCard(true)}
                  onStale={checkAgain}
               />
            ) : (
               <CardSetup organizationId={organizationId} status={status} onDone={handleCardSaved} />
            );
         // An ended subscription, and Enterprise picked with its terms not taken
         // up — the offer, picked to start with, or our team's word that it is
         // coming: every plan of either kind. Picking another drops the request.
         case BillingAction.RESUBSCRIBE:
         case BillingAction.ENTERPRISE_OFFER:
            return (
               <PlanPicker
                  organizationId={organizationId}
                  organizationLevel={getCurrentOrganizationLevel()}
                  offersTrial={offersTrial(status)}
                  billingStatus={status}
                  onSubscribed={handlePlanPicked}
               />
            );
         case BillingAction.ADD_CARD:
            // A plan picked with no trial left and no card can be picked again:
            // POST subscriptions replaces the pick (PAYMENT_METHOD_REQUIRED).
            // Every plan is offered, as for an ended subscription; a pick of
            // the other kind sets the level again.
            if (status?.reason === BillingReason.PAYMENT_METHOD_REQUIRED) {
               return isRepickingPlan ? (
                  <>
                     <p className="billing-note billing-note--center">
                        Picking a plan replaces the one you picked before, promo code included, so
                        check the code before you continue.
                     </p>
                     <PlanPicker
                        organizationId={organizationId}
                        organizationLevel={getCurrentOrganizationLevel()}
                        offersTrial={offersTrial(status)}
                        billingStatus={status}
                        initialPromoCode={pickedPromoCode}
                        onSubscribed={handlePlanRepicked}
                     />
                     <div className="billing-actions billing-actions--center">
                        <Button className="defaultButton" onClick={() => setIsChoosingPlan(false)}>
                           Keep the plan I picked
                        </Button>
                     </div>
                  </>
               ) : (
                  <>
                     {/* Keyed on the plan, so a pick made anywhere starts it
                         afresh: its setup intent and the agreement recorded
                         with it are for the plan the status names when they
                         are asked for. */}
                     <CardSetup
                        key={`${status.packetId}:${status.isMonthly}`}
                        organizationId={organizationId}
                        status={status}
                        onDone={handleCardSaved}
                     />
                     <div className="billing-actions billing-actions--center">
                        <Button className="defaultButton" onClick={() => setIsChoosingPlan(true)}>
                           Choose a different plan
                        </Button>
                     </div>
                  </>
               );
            }

            // A payment that failed — unpaid, or past_due with its grace period
            // over — is settled on the plan the organization has: a card, and
            // the amount owed shown before it is charged (amountDue.js). Never
            // the plans again. The card on file can be tried once more too.
            return (
               <>
                  <CardSetup
                     organizationId={organizationId}
                     status={status}
                     onDone={handleCardSaved}
                  />
                  {status?.reason === BillingReason.PAYMENT_FAILED && (
                     <RetrySavedCard
                        organizationId={organizationId}
                        status={status}
                        onResolved={handleResolved}
                        onStale={checkAgain}
                     />
                  )}
               </>
            );
         default:
            return (
               <p className="billing-panel__text">
                  This can't be sorted out from here. Please contact us at{" "}
                  <a href="mailto:info@kaptechgroup.com">info@kaptechgroup.com</a> and we'll help.
               </p>
            );
      }
   };

   return (
      <BillingShell
         organizationName={organizationName}
         hasOtherOrganizations={hasOtherOrganizations}
         isWide={showsPlans}
      >
         {isLoading ? (
            <div className="billing-loading">
               <Spin size="large" />
            </div>
         ) : isAccessHeld ? (
            <AccessHeldPanel
               organizationName={organizationName}
               onTryAgain={() => useBillingStore.getState().releaseGateHold(organizationId)}
            />
         ) : (
            <>
               <ReasonBanner status={status} />

               {loadError && (
                  <div className="billing-form__message" role="alert">
                     {describeBillingError(loadError)}{" "}
                     <Button type="link" onClick={checkAgain}>
                        Try again
                     </Button>
                  </div>
               )}

               {paymentRefusal && (
                  <div className="billing-form__message" role="alert">
                     {paymentRefusal}
                  </div>
               )}

               <section className={`billing-card${showsPlans ? " billing-card--plain" : ""}`}>
                  {isWaiting ? (
                     <div className="billing-waiting">
                        <Spin />
                        <p>Stripe is confirming your subscription. This usually takes a few seconds.</p>
                     </div>
                  ) : (
                     <React.Fragment key={refusals}>{renderAction()}</React.Fragment>
                  )}

                  {stillWaiting && (
                     <p className="billing-note">
                        Stripe hasn't confirmed it yet. It can take a few minutes — check again, or
                        come back to this page later.{" "}
                        <Button type="link" onClick={checkAgain}>
                           Check again
                        </Button>
                     </p>
                  )}
               </section>

               {/* A monthly plan left unpaid never ends on its own, so it can be
                   ended here: at once, with its open invoice voided. Its answer
                   replaces the status, and the page shows an ended subscription.
                   An annual plan's months stay due until its commitment ends, so
                   ending it goes through support instead. */}
               {offersCancelOnBillingPage(status) && !isWaiting && (
                  <p className="billing-note billing-note--center">
                     <Button type="link" onClick={() => setIsCancelling(true)}>
                        Cancel subscription
                     </Button>
                  </p>
               )}
               {cancelGoesThroughSupport(status) && !isWaiting && (
                  <p className="billing-note billing-note--center">
                     Your annual plan is a 12-month commitment, and its monthly payments stay due until
                     it ends. To cancel it, contact support at{" "}
                     <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>.
                  </p>
               )}
               {isCancelling && (
                  <CancelSubscription
                     isModalOpen={isCancelling}
                     handleAccountModal={() => setIsCancelling(false)}
                     handleCancelPlan={() => {}}
                     billingManaged
                     billingStatus={status}
                     announceServerMessage
                  />
               )}
            </>
         )}
      </BillingShell>
   );
}

export default BillingRequiredPage;
